import React from 'react';

// Generic loading-skeleton helpers for any lazy PrimeReact `DataTable`. While a page/sort fetch is in
// flight, feed `makeSkeletonRows(pageSize)` as the table `value` and pass the columns through
// `withSkeletonBodies` — the SAME columns then render a shimmer bar (`.table-skeleton-cell`) per cell
// instead of real data, so there is no separate skeleton-table markup to keep in sync.
//
// Usage:
//   const showSkeleton = isFetching && isPlaceholderData; // navigation only, not mutation refetches
//   <DataTable value={showSkeleton ? makeSkeletonRows(pageSize) : items}>
//     {withSkeletonBodies(buildColumns(items))}
//   </DataTable>

const SKELETON_FLAG = '__skeleton';

export const isSkeletonRow = (row: unknown): boolean =>
  !!row && typeof row === 'object' && (row as Record<string, unknown>)[SKELETON_FLAG] === true;

// Placeholder rows for a lazy DataTable. Negative ids keep them out of selection/mutation tracking and
// give stable React keys. Generic over the row type — the caller asserts its own row type.
export const makeSkeletonRows = <T,>(n: number): T[] =>
  Array.from(
    { length: n },
    (_, i) => ({ id: -1 - i, [SKELETON_FLAG]: true })
  ) as unknown as T[];

// Wrap every column's `body` so a skeleton placeholder row renders a shimmer bar; real rows are
// untouched. Columns without a `body` (rare) pass through unchanged.
export const withSkeletonBodies = (columns: React.ReactElement[]): React.ReactElement[] =>
  columns.map((col) => {
    const body = (col.props as { body?: (row: unknown) => React.ReactNode }).body;
    if (!body) return col;
    return React.cloneElement(col, {
      body: (row: unknown) =>
        isSkeletonRow(row) ? <span className="table-skeleton-cell" /> : body(row),
    } as Partial<typeof col.props>);
  });
